import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Clock, Zap } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Logo } from "../components/Logo";
import { eur } from "../lib/format";
import * as apiClient from "../api";

export default function Display() {
  const { data } = useQuery({ queryKey: ["display"], queryFn: apiClient.getDisplay, refetchInterval: 60000 });
  const [slide, setSlide] = useState(0);
  const slides = 3;

  useEffect(() => {
    const iv = setInterval(() => setSlide((s) => (s + 1) % slides), 8000);
    return () => clearInterval(iv);
  }, []);

  if (!data) return <div className="min-h-screen bg-[#0A1128] flex items-center justify-center text-white">Caricamento...</div>;

  return (
    <div className="min-h-screen bg-[#0A1128] text-white overflow-hidden relative">
      <div className="absolute top-0 inset-x-0 flex items-center justify-between p-8 z-10">
        <Logo light />
        <div className="text-right">
          <div className="font-accent italic text-secondary text-3xl">Vivi Vieste. Scopri il Gargano.</div>
          <div className="text-white/40 text-sm mt-1">{new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}</div>
        </div>
      </div>

      <div className="pt-32 px-12 pb-12 min-h-screen flex flex-col">
        {slide === 0 && (
          <div className="animate-fade flex-1">
            <h1 className="text-6xl font-heading font-semibold mb-8">Domani a Vieste</h1>
            <div className="grid grid-cols-3 gap-6">
              {data.tomorrow.slice(0, 6).map((e) => (
                <div key={e.id} className="rounded-3xl overflow-hidden bg-white/5 border border-white/10">
                  <div className="relative h-48"><img src={e.image} alt="" className="h-full w-full object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] to-transparent" /></div>
                  <div className="p-5">
                    <h3 className="text-2xl font-heading font-semibold mb-3 line-clamp-1">{e.title}</h3>
                    {e.slots.slice(0, 3).map((s) => (
                      <div key={s.time} className="flex items-center justify-between text-lg mb-1">
                        <span className="flex items-center gap-2"><Clock className="h-5 w-5 text-secondary" />{s.time}</span>
                        <span className={`font-bold ${s.status === "few" ? "text-accent" : "text-emerald-400"}`}>{s.status === "few" ? `LAST ${s.available}` : "AVAILABLE"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {slide === 1 && (
          <div className="animate-fade flex-1">
            <h1 className="text-6xl font-heading font-semibold mb-8 flex items-center gap-4"><Zap className="h-12 w-12 text-accent" /> Last Minute</h1>
            <div className="grid grid-cols-3 gap-6">
              {data.last_minute.slice(0, 6).map((e) => (
                <div key={e.id} className="rounded-3xl overflow-hidden bg-white/5 border border-white/10">
                  <div className="relative h-56"><img src={e.image} alt="" className="h-full w-full object-cover" /><span className="absolute top-4 right-4 rounded-full bg-accent px-4 py-1.5 text-lg font-bold">{e.time}</span></div>
                  <div className="p-5">
                    <h3 className="text-2xl font-heading font-semibold line-clamp-1">{e.title}</h3>
                    <div className="text-accent text-xl font-bold mt-1">Ultimi {e.seats_left} posti</div>
                    <div className="text-3xl font-heading font-bold mt-2">{eur(e.discount_price)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {slide === 2 && (
          <div className="animate-fade flex-1">
            <h1 className="text-6xl font-heading font-semibold mb-8">Esperienze in evidenza</h1>
            <div className="grid grid-cols-4 gap-6">
              {data.featured.slice(0, 8).map((e) => (
                <div key={e.id} className="rounded-3xl overflow-hidden bg-white/5 border border-white/10">
                  <div className="relative h-44"><img src={e.images[0]} alt="" className="h-full w-full object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] to-transparent" /></div>
                  <div className="p-4"><h3 className="text-xl font-heading font-semibold line-clamp-1">{e.title}</h3><div className="text-2xl font-bold text-secondary mt-1">{eur(e.price)}</div></div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-8">
          <div className="flex items-center gap-4 bg-white rounded-2xl p-4">
            <QRCodeSVG value={typeof window !== "undefined" ? window.location.origin : "https://cristaldaexperience.it"} size={90} fgColor="#004B87" />
            <div className="text-[#0A1128]"><div className="font-heading font-bold text-2xl">Scansiona & Prenota</div><div className="text-slate-500">cristaldaexperience.it</div></div>
          </div>
          <div className="flex gap-2">{Array.from({ length: slides }).map((_, i) => <div key={`slide-dot-${i}`} className={`h-2 rounded-full transition-all ${i === slide ? "w-10 bg-secondary" : "w-2 bg-white/30"}`} />)}</div>
          <div className="flex gap-8 text-white/50">{data.sponsors.slice(0, 3).map((s) => <span key={s.id} className="font-heading font-semibold text-xl">{s.company}</span>)}</div>
        </div>
      </div>
    </div>
  );
}
